import { Button } from "@/components/ui/button";
import { ArrowRight, CheckCircle2, Clock } from "lucide-react";
import { useScrollAnimation } from "@/hooks/useScrollAnimation";
import { useEffect, useState } from "react";

export const HeroSection = () => {
  const { ref: metricsRef, isVisible: metricsVisible } = useScrollAnimation({
    threshold: 0.2,
    rootMargin: "0px",
  });

  const [scrollY, setScrollY] = useState(0);
  const [isLoaded, setIsLoaded] = useState(false);

  useEffect(() => {
    // Trigger animations after mount
    const timer = setTimeout(() => setIsLoaded(true), 50);
    
    const handleScroll = () => {
      setScrollY(window.scrollY);
    };
    window.addEventListener("scroll", handleScroll, { passive: true });
    return () => {
      window.removeEventListener("scroll", handleScroll);
      clearTimeout(timer);
    };
  }, []);

  // Calculate scroll-based fade and scale
  // Use viewport-relative thresholds so CTA stays visible on small screens
  const vh = typeof window !== 'undefined' ? window.innerHeight : 800;
  const fadeStart = vh * 0.55; // Start fading after 55% of viewport height
  const fadeRange = vh * 0.45; // Complete fade over next 45%
  const scrollProgress = Math.min(Math.max((scrollY - fadeStart) / fadeRange, 0), 1);
  const contentOpacity = 1 - scrollProgress * 0.9;
  const contentScale = 1 - scrollProgress * 0.1;
  const contentTranslateY = scrollProgress * 40;


  return (
    <section className="relative min-h-screen flex items-center bg-background overflow-hidden">
      {/* Background Elements - counter-parallax (moves up as user scrolls down) */}
      <div className="absolute inset-0">
        {/* Base gradient for warmth - slowest counter-parallax */}
        <div 
          className="absolute inset-0 transition-opacity duration-1000 will-change-transform"
          style={{
            opacity: isLoaded ? 1 : 0,
            transform: `translateY(${scrollY * -0.08}px) scale(${1 + scrollProgress * 0.05})`,
            background: `
              radial-gradient(ellipse 80% 50% at 50% 0%, hsl(var(--primary) / 0.03) 0%, transparent 60%),
              radial-gradient(ellipse 60% 40% at 0% 50%, hsl(var(--primary) / 0.02) 0%, transparent 50%),
              radial-gradient(ellipse 60% 40% at 100% 50%, hsl(var(--primary) / 0.02) 0%, transparent 50%)
            `,
          }}
        />
        
        {/* Large grid - primary structure - medium counter-parallax */}
        <div 
          className="absolute inset-[-20%] transition-opacity duration-1500 will-change-transform"
          style={{
            opacity: isLoaded ? 1 - scrollProgress * 0.3 : 0,
            transitionDelay: '200ms',
            transform: `translateY(${scrollY * -0.12}px) scale(${1 + scrollProgress * 0.08})`,
            backgroundImage: `
              linear-gradient(hsl(var(--primary) / 0.06) 1px, transparent 1px),
              linear-gradient(90deg, hsl(var(--primary) / 0.06) 1px, transparent 1px)
            `,
            backgroundSize: '80px 80px',
            maskImage: 'radial-gradient(ellipse 70% 70% at 50% 40%, black 20%, transparent 70%)',
            WebkitMaskImage: 'radial-gradient(ellipse 70% 70% at 50% 40%, black 20%, transparent 70%)',
          }}
        />
        
        {/* Small grid - detail layer - faster counter-parallax */}
        <div 
          className="absolute inset-[-10%] transition-opacity duration-1500 will-change-transform"
          style={{
            opacity: isLoaded ? 1 - scrollProgress * 0.5 : 0,
            transitionDelay: '400ms',
            transform: `translateY(${scrollY * -0.18}px) scale(${1 + scrollProgress * 0.1})`,
            backgroundImage: `
              linear-gradient(hsl(var(--primary) / 0.03) 1px, transparent 1px),
              linear-gradient(90deg, hsl(var(--primary) / 0.03) 1px, transparent 1px)
            `,
            backgroundSize: '20px 20px',
            maskImage: 'radial-gradient(ellipse 60% 50% at 50% 50%, black 10%, transparent 60%)',
            WebkitMaskImage: 'radial-gradient(ellipse 60% 50% at 50% 50%, black 10%, transparent 60%)',
          }}
        />
        
        {/* Intersection glow points - fastest counter-parallax for depth */}
        <div 
          className="absolute inset-[-15%] transition-opacity duration-1500 will-change-transform"
          style={{
            opacity: isLoaded ? 1 - scrollProgress * 0.6 : 0,
            transitionDelay: '600ms',
            transform: `translateY(${scrollY * -0.25}px) scale(${1 + scrollProgress * 0.12})`,
            backgroundImage: `radial-gradient(circle at center, hsl(var(--primary) / 0.08) 1px, transparent 1px)`,
            backgroundSize: '80px 80px',
            maskImage: 'radial-gradient(ellipse 50% 40% at 50% 45%, black 0%, transparent 60%)',
            WebkitMaskImage: 'radial-gradient(ellipse 50% 40% at 50% 45%, black 0%, transparent 60%)',
          }}
        />
        
        {/* Vignette for depth - static */}
        <div 
          className="absolute inset-0"
          style={{
            background: `radial-gradient(ellipse 100% 100% at 50% 50%, transparent 30%, hsl(var(--background)) 100%)`,
          }}
        />
      </div>

      <div className="container max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pt-40 sm:pt-52 lg:pt-64 pb-20 sm:pb-28 relative z-10">
        <div 
          className="max-w-4xl mx-auto text-center will-change-transform"
          style={{
            opacity: contentOpacity,
            transform: `scale(${contentScale}) translateY(${contentTranslateY}px)`,
            transition: 'opacity 0.1s ease-out, transform 0.1s ease-out',
          }}
        >

          {/* Subtle focus zone around headline */}
          <div 
            className="absolute inset-0 pointer-events-none"
            style={{
              background: `radial-gradient(ellipse 50% 35% at 50% 35%, hsl(var(--primary) / 0.05) 0%, transparent 70%)`,
            }}
          />

          {/* Main Headline - monolithic, declarative end-state */}
          <h1 
            className={`relative text-4xl sm:text-5xl md:text-6xl lg:text-7xl xl:text-8xl font-serif text-primary leading-[1.1] sm:leading-[1.08] tracking-tight mb-8 sm:mb-10 transition-all duration-700 ease-out ${
              isLoaded ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-6'
            }`}
            style={{ 
              transitionDelay: '100ms',
              textShadow: `
                0 1px 2px hsl(var(--foreground) / 0.05),
                0 4px 12px hsl(var(--foreground) / 0.04),
                0 12px 32px hsl(var(--primary) / 0.08)
              `,
            }}
          >
            {/* First word - heavier weight for emphasis with animated accent */}
            <span className="relative font-bold inline-block">
              Entscheidung.
              <span 
                className="absolute inset-0 bg-gradient-to-b from-foreground/[0.03] to-transparent bg-clip-text pointer-events-none select-none"
                aria-hidden="true"
              />
              {/* Animated accent line with pulse */}
              <span 
                className={`absolute -bottom-2 sm:-bottom-3 left-0 h-[2px] sm:h-[3px] bg-primary rounded-full transition-all duration-1000 ease-out ${
                  isLoaded ? 'w-full animate-[accent-pulse_4s_ease-in-out_infinite_1.6s]' : 'w-0'
                }`}
                style={{ 
                  transitionDelay: '600ms',
                }}
                aria-hidden="true"
              />
              {/* Glow layer for accent line */}
              <span 
                className={`absolute -bottom-2 sm:-bottom-3 left-0 h-[2px] sm:h-[3px] bg-primary/60 rounded-full blur-sm transition-all duration-1000 ease-out ${
                  isLoaded ? 'w-full animate-[accent-glow-pulse_4s_ease-in-out_infinite_1.6s]' : 'w-0'
                }`}
                style={{ 
                  transitionDelay: '600ms',
                }}
                aria-hidden="true"
              />
            </span>
            <br />
            {/* Second word - slightly lighter for hierarchy */}
            <span className="relative font-semibold text-foreground/90 mt-1 sm:mt-2 inline-block">
              Geklärt.
            </span>
            
            {/* Central glow effect under headline */}
            <span 
              className={`absolute -bottom-4 sm:-bottom-6 left-1/2 -translate-x-1/2 w-[90%] sm:w-3/4 h-16 sm:h-20 transition-opacity duration-1000 pointer-events-none ${
                isLoaded ? 'opacity-100' : 'opacity-0'
              }`}
              style={{ 
                transitionDelay: '500ms',
                background: `radial-gradient(ellipse 100% 100% at 50% 0%, hsl(var(--primary) / 0.12) 0%, hsl(var(--primary) / 0.04) 40%, transparent 70%)`,
                filter: 'blur(20px)',
              }}
              aria-hidden="true"
            />
          </h1>

          {/* Subheadline — A) Verantwortung explizit */}
          <p 
            className={`text-base sm:text-lg md:text-xl text-muted-foreground/80 font-light leading-relaxed max-w-2xl mx-auto mb-4 sm:mb-5 px-4 transition-all duration-700 ease-out ${
              isLoaded ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-6'
            }`}
            style={{ transitionDelay: '200ms' }}
          >
            Quentari ermöglicht klare Entscheidungen mit dokumentierter Verantwortung. Tragfähiger Report für Board, Führung und Umsetzung.
          </p>

          {/* B) Problem-Callout — Schmerz konkret machen */}
          <p 
            className={`text-sm sm:text-base text-muted-foreground/60 leading-relaxed max-w-xl mx-auto mb-8 sm:mb-10 px-4 transition-all duration-700 ease-out ${
              isLoaded ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-6'
            }`}
            style={{ transitionDelay: '230ms' }}
          >
            Unklare Entscheidungen binden Budgets, Teams und Momentum.
          </p>

          {/* Value Metrics - näher an der Subheadline */}
          <div 
            ref={metricsRef as React.RefObject<HTMLDivElement>}
            className={`max-w-2xl mx-auto px-2 sm:px-0 mb-10 sm:mb-14 transition-all duration-700 ease-out ${
              isLoaded ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-6'
            }`}
            style={{ transitionDelay: '250ms' }}
          >
            {/* Desktop: side by side, Mobile: stacked */}
            <div className="hidden sm:flex items-center justify-center">
              <MetricCard 
                icon={Clock} 
                value="~60" 
                unit="Min" 
                label="strukturierter Entscheidungsprozess"
                isVisible={metricsVisible}
                delay={0}
              />
              <AnimatedDivider isVisible={metricsVisible} delay={60} className="mx-6 sm:mx-10" />
              <MetricCard 
                icon={CheckCircle2} 
                value="1" 
                unit="Entscheidungsreport" 
                label="Annahmen, Trade-offs, Verantwortliche, nächster Schritt"
                isVisible={metricsVisible}
                delay={120}
              />
            </div>
            <div className="flex sm:hidden flex-col items-center gap-2">
              <MetricCard 
                icon={Clock} 
                value="~60" 
                unit="Min" 
                label="strukturierter Entscheidungsprozess"
                isVisible={metricsVisible}
                delay={0}
              />
              <AnimatedDivider isVisible={metricsVisible} delay={60} orientation="horizontal" />
              <MetricCard 
                icon={CheckCircle2} 
                value="1" 
                unit="Entscheidungsreport" 
                label="Annahmen, Trade-offs, Verantwortliche, nächster Schritt"
                isVisible={metricsVisible}
                delay={120}
              />
            </div>
          </div>

          {/* CTA - funktional, eindeutig */}
          <div 
            className={`flex flex-col sm:flex-row items-center gap-4 sm:gap-6 justify-center mb-16 sm:mb-20 px-4 sm:px-0 transition-all duration-700 ease-out ${
              isLoaded ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-6'
            }`}
            style={{ transitionDelay: '300ms' }}
          >
            <Button
              size="lg"
              className="h-12 sm:h-14 px-8 sm:px-12 text-sm sm:text-base font-medium group shadow-lg shadow-primary/15 hover:shadow-xl hover:shadow-primary/25 hover:-translate-y-0.5 transition-all duration-300 w-full sm:w-auto"
              onClick={() => window.location.href = '/auth'}
            >
              Erste Entscheidung strukturieren
              <ArrowRight className="ml-2 w-4 h-4 sm:w-5 sm:h-5 transition-transform duration-300 group-hover:translate-x-1" />
            </Button>
            <button
              className="text-sm sm:text-base text-muted-foreground hover:text-primary transition-colors duration-300 underline underline-offset-4 decoration-muted-foreground/30 hover:decoration-primary/50"
              onClick={() => document.getElementById('process')?.scrollIntoView({ behavior: 'smooth' })}
            >
              Der Prozess
            </button>
          </div>

        </div>

        {/* Decorative Elements - Hidden on mobile, subtle floating animation */}
        <div 
          className="absolute top-1/4 left-8 w-px h-32 bg-gradient-to-b from-transparent via-primary/20 to-transparent hidden lg:block animate-[float_8s_ease-in-out_infinite]"
          style={{ 
            transform: `translateY(${scrollY * 0.15}px)`,
            animationDelay: '0s'
          }}
        />
        <div 
          className="absolute top-1/3 right-8 w-px h-24 bg-gradient-to-b from-transparent via-primary/15 to-transparent hidden lg:block animate-[float_10s_ease-in-out_infinite]"
          style={{ 
            transform: `translateY(${scrollY * 0.1}px)`,
            animationDelay: '2s'
          }}
        />
        <div 
          className="absolute bottom-1/4 left-16 w-24 h-px bg-gradient-to-r from-transparent via-primary/15 to-transparent hidden lg:block animate-[float-horizontal_9s_ease-in-out_infinite]"
          style={{ 
            transform: `translateY(${scrollY * -0.08}px)`,
            animationDelay: '1s'
          }}
        />
        <div 
          className="absolute top-1/2 right-16 w-16 h-px bg-gradient-to-r from-transparent via-primary/10 to-transparent hidden lg:block animate-[float-horizontal_11s_ease-in-out_infinite]"
          style={{ 
            transform: `translateY(${scrollY * 0.12}px)`,
            animationDelay: '3s'
          }}
        />
        <div 
          className="absolute bottom-1/3 right-24 w-px h-20 bg-gradient-to-b from-transparent via-primary/10 to-transparent hidden lg:block animate-[float_12s_ease-in-out_infinite]"
          style={{ 
            transform: `translateY(${scrollY * -0.05}px)`,
            animationDelay: '4s'
          }}
        />
      </div>

      {/* Scroll indicator - hidden when scrolled */}
      <div 
        className={`absolute bottom-8 sm:bottom-12 left-1/2 -translate-x-1/2 flex flex-col items-center gap-2 transition-all duration-500 cursor-pointer group ${
          isLoaded && scrollY < 50 ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-4 pointer-events-none'
        }`}
        onClick={() => document.getElementById('process')?.scrollIntoView({ behavior: 'smooth' })}
      >
        <span className="text-[10px] sm:text-xs text-muted-foreground/50 uppercase tracking-[0.2em] font-medium group-hover:text-muted-foreground/70 transition-colors">
          Scroll
        </span>
        <div className="relative w-5 h-8 sm:w-6 sm:h-10 rounded-full border border-muted-foreground/20 group-hover:border-muted-foreground/40 transition-colors">
          {/* Animated scroll dot */}
          <div 
            className="absolute left-1/2 -translate-x-1/2 top-1.5 sm:top-2 w-1 h-1.5 sm:w-1.5 sm:h-2 rounded-full bg-primary/60 animate-[scroll-bounce_2s_ease-in-out_infinite]"
          />
        </div>
        {/* Subtle glow on hover */}
        <div className="absolute -inset-4 rounded-full bg-primary/5 opacity-0 group-hover:opacity-100 transition-opacity blur-xl pointer-events-none" />
      </div>

      {/* Bottom fade - sanfter Übergang zur nächsten Sektion */}
      <div className="absolute bottom-0 left-0 right-0 h-40 sm:h-56 bg-gradient-to-t from-background via-background/80 to-transparent pointer-events-none" />
      
      {/* Transition accent line */}
      <div className="absolute bottom-0 left-1/2 -translate-x-1/2 w-24 sm:w-32 h-px bg-gradient-to-r from-transparent via-primary/20 to-transparent" />
    </section>
  );
};

interface MetricCardProps {
  icon: React.ElementType;
  value: string;
  unit: string;
  label: string;
  isVisible: boolean;
  delay: number;
}

const useCountUp = (end: number, isVisible: boolean, delay: number, duration: number = 1500) => {
  const [count, setCount] = useState(0);
  const [hasStarted, setHasStarted] = useState(false);

  useEffect(() => {
    if (!isVisible || hasStarted) return;
    
    const startDelay = setTimeout(() => {
      setHasStarted(true);
      const startTime = Date.now();
      const startValue = 0;
      
      const animate = () => {
        const elapsed = Date.now() - startTime;
        const progress = Math.min(elapsed / duration, 1);
        
        // Easing function: easeOutExpo
        const easeOut = progress === 1 ? 1 : 1 - Math.pow(2, -10 * progress);
        const currentValue = Math.round(startValue + (end - startValue) * easeOut);
        
        setCount(currentValue);
        
        if (progress < 1) {
          requestAnimationFrame(animate);
        }
      };
      
      requestAnimationFrame(animate);
    }, delay);
    
    return () => clearTimeout(startDelay);
  }, [isVisible, end, delay, duration, hasStarted]);

  return count;
};

const MetricCard = ({ icon: Icon, value, unit, label, isVisible, delay }: MetricCardProps) => {
  // Parse numeric value (handles "100%" and "60" etc.)
  const numericValue = parseInt(value.replace(/[^0-9]/g, ''), 10) || 0;
  const hasPercent = value.includes('%');
  const animatedValue = useCountUp(numericValue, isVisible, delay);
  
  return (
    <div 
      className={`group text-center p-4 sm:p-5 cursor-default transition-all duration-700 ease-[cubic-bezier(0.22,1,0.36,1)] ${
        isVisible 
          ? 'opacity-100 translate-y-0 scale-100' 
          : 'opacity-0 translate-y-6 scale-95'
      }`}
      style={{ transitionDelay: `${delay}ms` }}
    >
      {/* Value - monolithic, dominant */}
      <div className="flex items-baseline justify-center gap-1 mb-2">
        <span className="text-3xl sm:text-4xl lg:text-5xl font-serif font-semibold text-foreground tabular-nums">
          {animatedValue}{hasPercent ? '%' : ''}
        </span>
        {unit && (
          <span className="text-sm sm:text-base font-medium text-muted-foreground/70">
            {unit}
          </span>
        )}
      </div>
      
      {/* Label - subtle, descriptive */}
      <div className="flex items-center justify-center gap-1.5">
        <Icon className="w-3 h-3 text-primary/40" />
        <p className="text-[10px] sm:text-xs text-muted-foreground/60 uppercase tracking-widest">
          {label}
        </p>
      </div>
    </div>
  );
};

// Animated divider component
interface AnimatedDividerProps {
  isVisible: boolean;
  delay: number;
  orientation?: 'vertical' | 'horizontal';
  className?: string;
}

const AnimatedDivider = ({ isVisible, delay, orientation = 'vertical', className = '' }: AnimatedDividerProps) => {
  const baseClasses = orientation === 'vertical' 
    ? 'w-px h-12 bg-gradient-to-b from-transparent via-border/40 to-transparent'
    : 'h-px w-32 sm:w-48 bg-gradient-to-r from-transparent via-border/30 to-transparent';
  
  return (
    <div 
      className={`${baseClasses} transition-all duration-700 ease-[cubic-bezier(0.22,1,0.36,1)] ${
        isVisible ? 'opacity-100 scale-100' : 'opacity-0 scale-50'
      } ${className}`}
      style={{ transitionDelay: `${delay + 100}ms` }}
    />
  );
};
